"use client";

import * as React from "react";
import { useState } from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import classNames from "classnames";
import { Loader2 } from "lucide-react";

export const buttonVariants = cva(
  "inline-flex items-center justify-center gap-3 whitespace-nowrap font-label text-[12px] font-medium tracking-[1.5px] uppercase rounded-full [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none disabled:cursor-not-allowed focus-visible:underline",
  {
    variants: {
      variant: {
        primary:
          "bg-neutral-950 text-neutral-0 hover:bg-neutral-900 active:bg-neutral-800 focus-visible:bg-neutral-800 disabled:bg-neutral-600 disabled:opacity-100",
        // Filled, not outlined. The design system's Secondary has its stroke
        // switched OFF and a warm elevated fill; the code had drifted to
        // white-with-a-black-outline. The fill is what separates it from the
        // cream page background, which is why removing the border needs the
        // fill rather than leaving a white-on-cream ghost. Interaction ramp is
        // shared with IconButton's `nav` variant, which already lands here.
        secondary:
          "bg-[var(--surface-elevated-dark)] text-neutral-950 hover:bg-[#dfd9d3] active:bg-[#d3cdc7] focus-visible:bg-[#d3cdc7] disabled:bg-[var(--stroke-muted)] disabled:opacity-100",
        tertiary:
          "text-neutral-950 hover:bg-neutral-950/[.08] active:bg-neutral-950/[.12] focus-visible:bg-neutral-950/[.12] disabled:bg-neutral-300 disabled:opacity-100",
        destructive:
          "bg-red-600 text-neutral-0 hover:bg-red-700 active:bg-red-800 focus-visible:bg-red-800 disabled:bg-red-400 disabled:opacity-100",
      },
      size: {
        default: "h-[46px] px-9",
        sm: "h-8 px-6",
        lg: "h-[46px] px-9",
        icon: "size-9",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "default",
    },
  },
);

type OnClickHandler =
  | (() => void)
  | (() => Promise<void>)
  | ((e: React.MouseEvent<HTMLButtonElement>) => void)
  | ((e: React.MouseEvent<HTMLButtonElement>) => Promise<void>);

type ButtonProps = Omit<React.ComponentProps<"button">, "onClick"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    onClick?: OnClickHandler;
    /** Shows loading spinner. When true, button is disabled. Auto-set when async onClick is running. */
    loading?: boolean;
    /** Text to show while loading. If not provided, shows children with spinner. */
    loadingText?: string;
    /** When true, hides children and shows only the spinner when loading. Useful for icon buttons. */
    hideContentWhenLoading?: boolean;
  };

export function Button({
  className,
  variant,
  size,
  asChild = false,
  onClick,
  disabled,
  loading: externalLoading,
  loadingText,
  hideContentWhenLoading,
  children,
  ...props
}: ButtonProps) {
  const [internalLoading, setInternalLoading] = useState(false);
  const loading = externalLoading || internalLoading;

  const handleClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    if (loading || disabled || !onClick) return;

    const result = onClick(e);

    // Only show loading state if onClick returns a Promise
    if (result instanceof Promise) {
      setInternalLoading(true);
      try {
        await result;
      } finally {
        setInternalLoading(false);
      }
    }
  };

  const Comp = asChild ? Slot : "button";

  const showChildren = !(loading && hideContentWhenLoading);

  return (
    <Comp
      data-slot="button"
      className={classNames(
        buttonVariants({ variant, size, className }),
        loading && "pointer-events-none",
      )}
      disabled={disabled}
      aria-disabled={disabled || loading}
      onClick={handleClick}
      {...props}
    >
      {loading && <Loader2 className="w-4 h-4 animate-spin" />}
      {loadingText && loading ? loadingText : showChildren && children}
    </Comp>
  );
}
